import { useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { CONFIRMATION_HANDLE_TYPES, SMS_ERROR_CODE_DISABLE, TWO_FACTOR_AUTH_TYPES } from 'components/common/constants';
import SwitchConfirmationMethodScreen, {
  getUnavailableConfirmationTypes,
  hasSwitchableConfirmationMethod
} from 'components/common/ActionConfirmation/components/SwitchConfirmationMethodScreen';
import { ConfirmationModalHeader } from 'components/common/ConfirmationModal/ConfirmationModalHeader';
import { getConfirmationSubtitle } from 'components/common/ConfirmationModal/getConfirmationSubtitle';
import { ConfirmCodeWrapper } from 'components/common/ConfirmCodeWrapper/ConfirmCodeWrapper';
import i18nContext from 'components/i18n-context';
import { isFullSecurityCode } from 'components/utils';
import {
  getAvailableConfirmationTypes,
  getConfirmationActionType,
  getConfirmationDestination
} from 'services/authUtils';
import { getErrorMessageForAlert } from 'services/utils';
import Button from 'uikit/Button/Button';
import Loader from 'components/common/Loader';
import './PopUpScheme.scss';

const PopUpInternalActionConfirmationScheme = ({
  isLoading,
  isShowSwitchConfirmationMethodScreen,
  title,
  confirmButtonLabel,
  onClose,
  onConfirm,
  error,
  clearError,
  resendSecurityCode,
  setIsShowSwitchConfirmationMethodScreen,
  handleForceSwitchToAnotherConfirmationType,
  confirmationId,
  module
}) => {
  const i18n = useContext(i18nContext);
  const [alertState, setAlertState] = useState({ type: '', message: '' });
  const [showCountDown, setShowCountDown] = useState(false);
  const [securityCode, setSecurityCode] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(true);
  const currentConfirmationType = getConfirmationActionType();
  const pushPollingIntervalRef = useRef(null);

  useEffect(() => {
    // Reset security code when confirmation type changes
    setSecurityCode('');
    setIsConfirmDisabled(true);

    if (currentConfirmationType !== TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH) {
      setShowCountDown(true);
    }

    // Start polling for push notification confirmation
    if (currentConfirmationType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION && confirmationId && module) {
      // For push notifications, we need to continuously call onConfirm with null code
      // The parent component will handle the polling logic and error responses
      pushPollingIntervalRef.current = setInterval(() => {
        onConfirm(null);
      }, 2000); // Poll every 2 seconds
    }

    return () => {
      clearError();
      if (pushPollingIntervalRef.current) {
        clearInterval(pushPollingIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConfirmationType, confirmationId, module]);

  const handleResendCode = async () => {
    clearError();
    setShowCountDown(true);
    setAlertState({ type: 'success', message: i18n.getMessage('pin.alert.sent') });
    isDisabled && setIsDisabled(false);
    await resendSecurityCode();
  };

  const onTimerEnd = () => {
    setShowCountDown(false);
  };

  const onComplete = (code) => {
    setSecurityCode(code);
    if (!isFullSecurityCode(code)) {
      setIsConfirmDisabled(true);
    } else {
      setIsConfirmDisabled(false);
      setAlertState({ type: '', message: '' });
    }
  };

  const handleConfirm = () => {
    // For push notifications, pass null as code
    if (currentConfirmationType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION) {
      onConfirm(null);
    } else {
      onConfirm(securityCode);
    }
  };

  // TRN-7254: this scheme's header now carries the "where did the code come from" copy (per
  // currentConfirmationType) instead of ConfirmCodeWrapper's own inline description — see
  // hideDescription below. `title` itself stays the caller's action-specific copy (e.g. "Payment
  // confirmation") unchanged; this is just the destination/method line under it.
  const destinationText = getConfirmationDestination(currentConfirmationType);
  const destination = destinationText ? <strong>{destinationText}</strong> : null;

  const availableConfirmationTypes = getAvailableConfirmationTypes();
  const unavailableConfirmationTypes = getUnavailableConfirmationTypes(error);

  useEffect(() => {
    if (SMS_ERROR_CODE_DISABLE?.includes(error?.code)) {
      setIsDisabled(true);
    }
    // Don't show alert for CONFIRMATION_PENDING_ACTION - it's normal during push notification polling
    if (error && error?.code !== 'CONFIRMATION_PENDING_ACTION') {
      setAlertState({ type: 'warning', message: getErrorMessageForAlert(i18n, error) });
      setIsConfirmDisabled(true);
    } else if (!error) {
      if (
        currentConfirmationType === TWO_FACTOR_AUTH_TYPES.SMS ||
        currentConfirmationType === TWO_FACTOR_AUTH_TYPES.WHATSAPP ||
        currentConfirmationType === TWO_FACTOR_AUTH_TYPES.EMAIL
      ) {
        isDisabled && setIsDisabled(false);
        setAlertState({ type: 'success', message: i18n.getMessage('pin.alert.sent') });
      } else if (currentConfirmationType !== TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION) {
        // Don't show "code sent" for push notifications
        setAlertState({ type: '', message: '' });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, currentConfirmationType]);

  return (
    <div
      className={`confirmation-modal confirmation-modal--shadow-${
        isShowSwitchConfirmationMethodScreen ? 'glow' : 'neutral'
      }`}
    >
      {isShowSwitchConfirmationMethodScreen ? (
        <SwitchConfirmationMethodScreen
          onSwitchMethod={(confirmationType) => {
            // Clear the security code and disable confirm button when switching methods
            setSecurityCode('');
            setIsConfirmDisabled(true);
            handleForceSwitchToAnotherConfirmationType(confirmationType);
          }}
          onBack={() => {
            // Clear the security code when going back without switching
            setSecurityCode('');
            setIsConfirmDisabled(true);
            setIsShowSwitchConfirmationMethodScreen(false);
          }}
          currentConfirmationType={currentConfirmationType}
          availableConfirmationTypes={availableConfirmationTypes}
          unavailableConfirmationTypes={unavailableConfirmationTypes}
        />
      ) : (
        <>
          <ConfirmationModalHeader
            title={title}
            subtitle={getConfirmationSubtitle(currentConfirmationType, destinationText, i18n)}
            onClose={onClose}
          />
          <div className={'confirmation-modal__body'}>
            <ConfirmCodeWrapper
              PhoneComponent={destination}
              isDisabled={isDisabled || currentConfirmationType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION}
              error={!error?.type && error}
              onComplete={onComplete}
              showCountDown={showCountDown}
              handleResendCode={handleResendCode}
              onTimerEnd={onTimerEnd}
              alertState={alertState}
              confirmationHandleType={CONFIRMATION_HANDLE_TYPES.ON_CHANGE}
              handleSwitchToAnotherMethod={() => {
                // Clear the code when opening switch method screen
                setSecurityCode('');
                setIsConfirmDisabled(true);
                setIsShowSwitchConfirmationMethodScreen(true);
              }}
              isSwitchToAnotherConfirmationMethod={hasSwitchableConfirmationMethod(
                currentConfirmationType,
                availableConfirmationTypes,
                unavailableConfirmationTypes
              )}
              hideDescription
            />

            {currentConfirmationType !== TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION && (
              <Button
                type={'primary'}
                size={'large'}
                fullWidth
                onClick={handleConfirm}
                isDisabled={isLoading || isConfirmDisabled}
              >
                {isLoading ? <Loader isText={false} /> : confirmButtonLabel}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

PopUpInternalActionConfirmationScheme.propTypes = {
  isLoading: PropTypes.bool,
  isShowSwitchConfirmationMethodScreen: PropTypes.bool,
  title: PropTypes.string,
  confirmButtonLabel: PropTypes.string,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  clearError: PropTypes.func,
  error: PropTypes.any,
  resendSecurityCode: PropTypes.func,
  setIsShowSwitchConfirmationMethodScreen: PropTypes.func,
  handleForceSwitchToAnotherConfirmationType: PropTypes.func,
  confirmationId: PropTypes.string,
  module: PropTypes.string
};

export default PopUpInternalActionConfirmationScheme;
